/**
 * A new tenant appeared. Platform-operator event: it is delivered to the platform
 * tenant's own channels, not to the tenant it is about.
 *
 * Emitted only once the tenant is durable — the Shopify claim rolls its tenant back when
 * the store fails to materialize, so the emit sits in the claim action, not in
 * `provisionTenantCore`.
 */

import type { PlatformEventDefinition, PlatformEventPayloadBase } from './definition'

export const PLATFORM_TENANT_REGISTERED_EVENT_TYPE = 'platform_tenant_registered' as const

/** How the tenant came into existence. */
export type TenantRegistrationSource = 'self_service' | 'shopify_install' | 'admin'

export interface PlatformTenantRegisteredPayload extends PlatformEventPayloadBase {
  adminEmail: string
  planName: string
  billingChannel: string
  source: TenantRegistrationSource
}

const SOURCES: readonly TenantRegistrationSource[] = ['self_service', 'shopify_install', 'admin']

const SOURCE_LABELS: Record<TenantRegistrationSource, string> = {
  self_service: 'Self-service signup',
  shopify_install: 'Shopify App Store install',
  admin: 'Created by platform staff',
}

function isPlatformTenantRegisteredPayload(
  payload: unknown,
): payload is PlatformTenantRegisteredPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.tenantId === 'string' &&
    typeof p.tenantName === 'string' &&
    typeof p.adminEmail === 'string' &&
    typeof p.planName === 'string' &&
    typeof p.billingChannel === 'string' &&
    SOURCES.includes(p.source as TenantRegistrationSource)
  )
}

export const platformTenantRegisteredEventDefinition: PlatformEventDefinition<PlatformTenantRegisteredPayload> =
  {
    eventType: PLATFORM_TENANT_REGISTERED_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isPlatformTenantRegisteredPayload,
    system: {
      label: 'New tenant registered',
      description:
        'A new tenant was created — by self-service signup, a Shopify App Store install, or platform staff.',
      summarize: (payload) => ({
        title: 'New tenant registered',
        facts: [
          { label: 'Tenant', value: payload.tenantName },
          { label: 'Admin', value: payload.adminEmail },
          { label: 'Plan', value: payload.planName },
          { label: 'Billing', value: payload.billingChannel },
          { label: 'Source', value: SOURCE_LABELS[payload.source] },
        ],
        href: '/?page=tenant-management',
      }),
    },
  }
